import {
	HTMLElementTag,
	RenderHideOption,
	Severity,
	TestingWorkflowFieldType,
	TestingWorkflowInfoHierarchyNodeType,
	TestingWorkflowInfoType,
	VALIDATION_RADIO_GROUP_OPTIONS,
	WorkflowStepType,
	WorkflowTransitionTriggerType,
} from "node-library/dist/constants";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import {
	evaluateFrontendRenderCondition,
	extractFieldInfoNodeIdsForCalculationFormula,
	extractFieldInfoNodeIdsForRenderConditions,
} from "node-library/dist/helpers/workflow-formula-evaluation-helper";
import {
	FrontendCompanyWithAdditionalInfomation,
	FrontendUserInfo,
} from "node-library/dist/portal";
import {
	FrontendBatchPageTestBatchDetails,
	FrontendBatchWorkflowSignature,
	FrontendTestResult,
} from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import {
	FrontendCompanyTestingWorkflowInfoFields,
	FrontendCompanyTestingWorkflowInfoHierarchyNodes,
	FrontendCompanyTestingWorkflowInfoNodesRenderConditions,
	FrontendCompanyTestingWorkflowTransitions,
	FrontendCompanyWorkflowResultFields,
	FrontendCompanyWorkflowSteps,
} from "node-library/dist/structures/frontendTestWorkflowStructureDefinitions";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	BlackButton,
	GreenButton,
} from "../../../../../common-components/button";
import { CustomDateTimePicker } from "../../../../../common-components/datePicker";
import {
	CustomTextInput,
	StandardRadioGroup,
	TableDropdown,
} from "../../../../../common-components/inputs";
import WorkflowResultDeviationModal from "../../../../../common-components/modals/workflowResultDeviationModal";
import WorkflowSignatureModal from "../../../../../common-components/modals/workflowSignatureModal";
import { RedErrorIcon } from "../../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../../common-components/toast/successIcons";
import { YellowWarnIcon } from "../../../../../common-components/toast/warnIcons";
import { sendPOSTRequest } from "../../../../../requestHelper/sendRequest";
import { WorkflowResultField } from "./workflowResultField";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	user: FrontendUserInfo;
	currentStep: FrontendCompanyWorkflowSteps;
	stepToRender: FrontendCompanyWorkflowSteps;
	batchDetails: FrontendBatchPageTestBatchDetails;
	reloadBatchDetails: () => void;
	setStepToRender: (step: FrontendCompanyWorkflowSteps) => void;
}

export function WorkflowStepDataContainer(props: Props) {
	const {
		user,
		company,
		currentStep,
		stepToRender,
		batchDetails,
		reloadBatchDetails,
		setStepToRender,
	} = props;
	const { openSnackbar } = useSnackbar();
	const { t: translation } = useTranslation();

	const rootNode: FrontendCompanyTestingWorkflowInfoHierarchyNodes | undefined =
		stepToRender.rootHierarchyNode;
	const batchEditDisabled: boolean = batchDetails.editDisabled;
	const isCurrentStep: boolean = currentStep.stepId === stepToRender.stepId;

	function collectInfoFieldsFromNode(
		node: FrontendCompanyTestingWorkflowInfoHierarchyNodes,
	): FrontendCompanyTestingWorkflowInfoFields[] {
		let fields = [...(node.infoFields ?? [])];

		for (const child of node.children ?? []) {
			fields = fields.concat(collectInfoFieldsFromNode(child));
		}

		return fields;
	}

	const infoFieldsMap = useMemo(() => {
		const map: Record<string, FrontendCompanyTestingWorkflowInfoFields> = {};

		const steps = batchDetails.workflowStructure?.workflowSteps ?? [];

		for (const step of steps) {
			const root = step.rootHierarchyNode;
			if (!root) continue;

			const fields = collectInfoFieldsFromNode(root);

			for (const f of fields) {
				map[f.infoId] = f;
			}
		}

		return map;
	}, [batchDetails.workflowStructure]);

	function getFieldValue(infoId: string, instanceId?: string): string {
		let result: string = "";
		if (instanceId) {
			result = fieldValues.instances[instanceId]?.[infoId] ?? "";
			if (!result) {
				return fieldValues.batch[infoId] ?? "";
			}
			return result;
		}
		return fieldValues.batch[infoId] ?? "";
	}

	const [fieldValues, setFieldValues] = React.useState<{
		batch: Record<string, string>; // no instanceId
		instances: Record<string, Record<string, string>>; // per instance
	}>(() => {
		const initial: {
			batch: Record<string, string>; // no instanceId
			instances: Record<string, Record<string, string>>; // per instance
		} = {
			batch: {},
			instances: {},
		};

		batchDetails.batchWorkflowInformation.batchWorkflowInfos.forEach((info) => {
			if (info.instanceId) {
				const i = info.instanceId;
				if (!initial.instances[i]) initial.instances[i] = {};
				initial.instances[i][info.infoNodeId] =
					info.value ?? info.selectedOptionNodeId ?? "";
			} else {
				initial.batch[info.infoNodeId] =
					info.value ?? info.selectedOptionNodeId ?? "";
			}
		});

		return initial;
	});
	const [resultValues, setResultValues] = useState<
		Record<string, Record<string, FrontendTestResult>> // <instanceId, <resultFieldId, result>>
	>(() =>
		batchDetails.batchWorkflowInformation.testResults.reduce(
			(acc, result) => {
				const instanceId = result.sampleInstanceId;
				const resultFieldId = result.resultFieldId;

				if (!acc[instanceId]) {
					acc[instanceId] = {};
				}

				acc[instanceId][resultFieldId] = result;
				return acc;
			},
			{} as Record<string, Record<string, FrontendTestResult>>,
		),
	);

	const [transitionInProgress, setTransitionInProgress] =
		useState<boolean>(false);
	function setFieldValue(
		infoNodeId: string,
		value: string,
		instanceId?: string,
	) {
		setFieldValues((prev) => {
			if (instanceId) {
				return {
					...prev,
					instances: {
						...prev.instances,
						[instanceId]: {
							...(prev.instances[instanceId] || {}),
							[infoNodeId]: value,
						},
					},
				};
			} else {
				return {
					...prev,
					batch: {
						...prev.batch,
						[infoNodeId]: value,
					},
				};
			}
		});
	}
	const updateResultValue = (
		instanceId: string,
		resultFieldId: string,
		newResult: FrontendTestResult,
	) => {
		setResultValues((prev) => ({
			...prev,
			[instanceId]: {
				...(prev[instanceId] ?? {}),
				[resultFieldId]: newResult,
			},
		}));
	};

	// Precompute liveValues for all instances
	const liveFormulaValuesByInstance = useMemo(() => {
		const map: Record<string, Record<string, Record<string, string>>> = {}; // instanceId, <resultFieldId, <relatedValueFieldId, value>>

		for (const instance of batchDetails.instances) {
			const instanceId = instance.sampleInstanceId;

			map[instanceId] = {};

			const allFields: Array<FrontendCompanyWorkflowResultFields> =
				batchDetails.workflowStructure?.resultFields ?? [];

			for (const field of allFields) {
				const formula = field.resultCalculationFormula;
				if (!formula) continue;

				const ids: Array<string> =
					extractFieldInfoNodeIdsForCalculationFormula(formula);
				const values: Record<string, string> = {};

				for (const id of ids) {
					const liveValue = getFieldValue(id, instanceId);
					const defaultValue = infoFieldsMap[id]?.defaultValue || "";
					values[id] = liveValue || defaultValue;
				}

				// store by resultFieldId
				map[instanceId][field.resultFieldId] = values;
			}
		}

		return map;
	}, [batchDetails.instances, batchDetails.workflowStructure, fieldValues]);

	const [deviationModalOpen, setDeviationModalOpen] = useState<boolean>(false);
	const [signModalOpen, setSignModalOpen] = useState<boolean>(false);
	const [signModalProceedFunction, setSignModalProceedFunction] = useState<
		() => void
	>(() => () => {});
	const [signModalDisableEdit, setSignModalDisableEdit] =
		useState<boolean>(false);

	const [resultToDeviate, setResultToDeviate] = useState<FrontendTestResult>();
	const [deviateInstanceId, setDeviateInstanceId] = useState<string>();

	const renderingStepTransitionIds: Array<string> =
		stepToRender.transitions.map((transition) => {
			return transition.transitionId;
		});
	const renderingStepSignatures: Array<FrontendBatchWorkflowSignature> =
		batchDetails.batchWorkflowInformation.batchWorkflowSignatures.filter(
			(signature) => {
				return renderingStepTransitionIds.includes(signature.transitionId);
			},
		);

	function validateCurrentStepMandatoryFields() {
		const renderingStepFields: Array<FrontendCompanyTestingWorkflowInfoFields> =
			stepToRender.rootHierarchyNode
				? collectInfoFieldsFromNode(stepToRender.rootHierarchyNode)
				: [];
		const mandatoryFields: Array<FrontendCompanyTestingWorkflowInfoFields> =
			renderingStepFields.filter((field) => {
				return field.mandatory;
			});
		for (const field of mandatoryFields) {
			switch (field.infoType) {
				case TestingWorkflowInfoType.INSTANCE:
					for (const instance of batchDetails.instances) {
						if (
							!getFieldValue(field.infoId, instance.sampleInstanceId) &&
							!field.defaultValue
						) {
							return false;
						}
					}
					break;

				case TestingWorkflowInfoType.BATCH:
					if (!getFieldValue(field.infoId) && !field.defaultValue) {
						return false;
					}
					break;
			}
		}
		return true;
	}
	async function handleTransitionConfirm(
		transition: FrontendCompanyTestingWorkflowTransitions,
	) {
		if (!validateCurrentStepMandatoryFields()) {
			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("WorkflowMissingInformationError"),
				<YellowWarnIcon />,
			);
			return;
		}
		setTransitionInProgress(true);
		try {
			const response = await sendPOSTRequest(
				"/workflow/workflow-transition-proceed",
				{
					batchId: batchDetails.testBatchId,
					transitionId: transition.transitionId,
					transitionAssociatedInfos: fieldValues,
					transitionAssociatedResults:
						stepToRender.stepType === WorkflowStepType.RESULT
							? resultValues
							: {},
				},
			);
			openSnackbar(
				translation("BatchStatusUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
			const nextStep = batchDetails.workflowStructure?.workflowSteps.find(
				(step) => {
					return step.stepId === transition.toStepId;
				},
			);
			if (nextStep) {
				setStepToRender(nextStep);
			}
			reloadBatchDetails();
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setTransitionInProgress(false);
		}
	}

	async function handleUpdateTransitionInfo() {
		if (!validateCurrentStepMandatoryFields()) {
			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("WorkflowMissingInformationError"),
				<YellowWarnIcon />,
			);
			return;
		}
		setTransitionInProgress(true);
		try {
			const response = await sendPOSTRequest(
				"/workflow/update-workflow-information",
				{
					batchId: batchDetails.testBatchId,
					stepId: stepToRender.stepId,
					transitionAssociatedInfos: fieldValues,
				},
			);
			openSnackbar(
				translation("BatchStatusUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);

			reloadBatchDetails();
		} catch (error: any) {
			console.error(error);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setTransitionInProgress(false);
		}
	}

	function renderWorkflowHierarchyNode(
		node: FrontendCompanyTestingWorkflowInfoHierarchyNodes,
		level: number,
	): JSX.Element {
		const nodeType: TestingWorkflowInfoHierarchyNodeType = node.nodeType;
		const elementTag: HTMLElementTag = node.elementTag || "DIV";
		const cssClassName: string | undefined = node.cssClassName;

		let nodeComponent: JSX.Element;
		if (nodeType === TestingWorkflowInfoHierarchyNodeType.INSTANCE_INFO_GROUP) {
			nodeComponent = renderWorkflowInstanceTable(node, level);
		} else {
			nodeComponent = renderWorkflowNonTableElements(node, level);
		}
		const transitionComponent: Array<JSX.Element> =
			stepToRender.fullIndex > currentStep.fullIndex ||
			(batchEditDisabled && stepToRender.fullIndex < currentStep.fullIndex)
				? []
				: stepToRender.transitions.map((transition) => {
						let component: JSX.Element;
						switch (transition.triggerType) {
							case WorkflowTransitionTriggerType.CONFIRM:
								const signatureOnClickFunction = () => {
									setSignModalOpen(true);
									setSignModalDisableEdit(transition.disableBatchEdit);
									setSignModalProceedFunction(() => () => {
										handleTransitionConfirm(transition);
									});
								};
								return (
									<GreenButton
										id="transition-confirm-button"
										children={
											isCurrentStep
												? transition.transitionName
												: translation("Update")
										}
										type="submit"
										onClick={() =>
											isCurrentStep
												? transition.needSignature
													? signatureOnClickFunction()
													: handleTransitionConfirm(transition)
												: handleUpdateTransitionInfo()
										}
										disabled={transitionInProgress}
									></GreenButton>
								);
							case WorkflowTransitionTriggerType.REJECT:
								return (
									<BlackButton
										id="transition-confirm-button"
										children={transition.transitionName}
										type="submit"
										onClick={() => console.log("You clicked me!!!!!!!!!")}
										disabled={transitionInProgress}
									></BlackButton>
								);
						}
					});

		return (
			<div style={{ display: "grid" }}>
				{nodeComponent}
				<div style={{ display: "flex", justifyContent: "flex-end" }}>
					{transitionComponent}
				</div>
			</div>
		);
	}

	function renderWorkflowNonTableElements(
		node:
			| FrontendCompanyTestingWorkflowInfoHierarchyNodes
			| FrontendCompanyTestingWorkflowInfoFields,
		level: number,
	): JSX.Element {
		if ("nodeId" in node) {
			// ✅ node is FrontendCompanyTestingWorkflowInfoHierarchyNodes here
			const childNodesAndFields: Array<
				| FrontendCompanyTestingWorkflowInfoHierarchyNodes
				| FrontendCompanyTestingWorkflowInfoFields
			> = [...node.children, ...node.infoFields].sort((a, b) => {
				const orderA =
					"nodeDisplayOrder" in a ? a.nodeDisplayOrder : a.displayOrder;
				const orderB =
					"nodeDisplayOrder" in b ? b.nodeDisplayOrder : b.displayOrder;
				return orderA - orderB;
			});

			const renderedChildren = childNodesAndFields.map((child) =>
				renderWorkflowNonTableElements(child, level + 1),
			);

			const elementTag: HTMLElementTag = node.elementTag || "DIV";
			const cssClassName: string | undefined = node.cssClassName;

			const element: JSX.Element = React.createElement(
				elementTag.toLowerCase(),
				{},
				<>
					<div
						style={{
							fontWeight: 600,
							marginBottom: 4,
						}}
					>
						{node.nodeName}
					</div>
					<div
						className={cssClassName}
						style={{
							paddingLeft: elementTag === "DIV" && level > 0 ? 8 : undefined,
						}}
					>
						{renderedChildren}
					</div>
				</>,
			);
			return element;
		} else {
			return renderWorkflowInputField(node);
		}
	}

	function renderWorkflowInputField(
		node: FrontendCompanyTestingWorkflowInfoFields,
		instanceId?: string,
	): JSX.Element {
		const fieldType: TestingWorkflowFieldType = node.fieldType;
		const value =
			getFieldValue(node.infoId, instanceId) || node.defaultValue || "";
		const setValue = (value: string) => {
			setFieldValue(node.infoId, value, instanceId);
		};
		let disabledByRenderCondition: boolean = false;
		const renderCondition:
			| FrontendCompanyTestingWorkflowInfoNodesRenderConditions
			| undefined = node.renderCondition;
		if (renderCondition) {
			const relatedInfoIds: Array<string> =
				extractFieldInfoNodeIdsForRenderConditions(renderCondition);

			const relatedValues: Record<string, string> = {};
			for (const infoId of relatedInfoIds) {
				relatedValues[infoId] = getFieldValue(infoId, instanceId);
			}
			const shouldRender: boolean = evaluateFrontendRenderCondition(
				renderCondition,
				relatedValues,
			);
			if (
				!shouldRender &&
				node.hideOption &&
				node.hideOption === RenderHideOption.HIDE
			) {
				return <></>; //do not render
			} else if (
				!shouldRender &&
				node.hideOption &&
				node.hideOption === RenderHideOption.DISABLE
			) {
				disabledByRenderCondition = true;
			}
		}

		switch (fieldType) {
			case TestingWorkflowFieldType.TEXT_INPUT:
			case TestingWorkflowFieldType.NUMERIC_INPUT:
				return (
					<CustomTextInput
						id={node.fieldKey}
						key={node.fieldKey}
						value={value}
						setValue={setValue}
						label={
							node.fieldName
								? node.fieldName + (node.mandatory ? " *" : "")
								: ""
						}
						// placeholder={translation("Confirm")}
						type="text"
						required={Boolean(node.mandatory)}
						disabled={
							!node.editable ||
							(batchEditDisabled &&
								stepToRender.fullIndex < currentStep.fullIndex) ||
							disabledByRenderCondition
						}
					></CustomTextInput>
				);
			case TestingWorkflowFieldType.VALIDATION_RADIO_GROUP:
				const radioOptions: Array<{
					key: string;
					name: string;
					value: any;
				}> = VALIDATION_RADIO_GROUP_OPTIONS.map((child) => {
					return {
						key: child,
						name: translation(child),
						value: child,
					};
				});
				return (
					<div key={node.fieldKey} className="">
						<div className="typography-body-02">
							{node.fieldName
								? node.fieldName + (node.mandatory ? " *" : "")
								: ""}
						</div>
						<StandardRadioGroup
							options={radioOptions}
							selectedOption={value}
							onChange={(e) => {
								setValue(e.target.value);
							}}
							className="test-additional-option-radio-group"
							disabled={
								!node.editable ||
								(batchEditDisabled &&
									stepToRender.fullIndex < currentStep.fullIndex) ||
								disabledByRenderCondition
							}
						/>
					</div>
				);
				break;
			case TestingWorkflowFieldType.DROPDOWN_INPUT:
				const dropdownOption = (node.dropdownOptions || []).map((option) => {
					return {
						title: option.optionKey,
						value: option.optionId,
					};
				});
				return (
					<TableDropdown
						id={node.fieldKey}
						label={node.fieldName}
						options={dropdownOption}
						value={value}
						setValue={setValue}
						disabled={
							!node.editable ||
							(batchEditDisabled &&
								stepToRender.fullIndex < currentStep.fullIndex) ||
							disabledByRenderCondition
						}
					></TableDropdown>
				);
			case TestingWorkflowFieldType.CHECKBOX_GROUP:
				return <></>;
			case TestingWorkflowFieldType.DATETIME_INPUT:
				let dateValue: Date | null = null;

				if (value) {
					// Try to parse the stored ISO string into a Date
					const parsed = new Date(value);
					if (!isNaN(parsed.getTime())) {
						dateValue = parsed;
					}
				}

				return (
					<div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
						<div className="typography-body-01">{node.fieldName}</div>
						<CustomDateTimePicker
							selectedDate={dateValue}
							setSelectedDate={(d: Date | null) => {
								// Convert Date back to string before storing
								setValue(d ? d.toISOString() : "");
							}}
							disabled={
								!node.editable ||
								(batchEditDisabled &&
									stepToRender.fullIndex < currentStep.fullIndex) ||
								disabledByRenderCondition
							}
							errorMsg={undefined} // you can replace this if you have a validation message
						/>
					</div>
				);
		}
	}
	function renderWorkflowInstanceTable(
		tableNode: FrontendCompanyTestingWorkflowInfoHierarchyNodes,
		level: number,
	): JSX.Element {
		const nodeType: TestingWorkflowInfoHierarchyNodeType = tableNode.nodeType;
		const elementTag: HTMLElementTag = tableNode.elementTag || "DIV";
		const cssClassName: string | undefined = tableNode.cssClassName;

		const sortedChildren = tableNode.children.sort((a, b) => {
			return a.nodeDisplayOrder - b.nodeDisplayOrder;
		});

		const table: JSX.Element = (
			<table>
				{renderTableHeaders(sortedChildren)}
				{renderTableBody(sortedChildren)}
			</table>
		);
		return table;
	}

	function renderTableHeaders(
		children: Array<FrontendCompanyTestingWorkflowInfoHierarchyNodes>,
	): JSX.Element {
		// Collect all rows as arrays of <th> elements
		const rows: Array<JSX.Element[]> = [];

		// Recursive helper to populate rows[level]
		function traverse(
			nodes: Array<FrontendCompanyTestingWorkflowInfoHierarchyNodes>,
			depth: number,
		) {
			if (!rows[depth]) rows[depth] = [];
			const sortedNodes = [...nodes].sort(
				(a, b) => a.nodeDisplayOrder - b.nodeDisplayOrder,
			);
			for (const node of sortedNodes) {
				// Create the <th> element for this node
				const th = (
					<th
						key={node.nodeId}
						rowSpan={node.rowSpan}
						colSpan={node.colSpan}
						className={node.cssClassName}
					>
						{node.nodeName}
					</th>
				);

				rows[depth].push(th);

				// If it has children, go deeper
				if (node.children && node.children.length > 0) {
					traverse(node.children, depth + 1);
				}
			}
		}

		traverse(children, 0);

		// Now construct <thead> with <tr> rows
		return (
			<thead>
				{rows.map((rowCells, i) => (
					<tr key={i}>{rowCells}</tr>
				))}
			</thead>
		);
	}
	function renderTableBody(
		rootNodes: Array<FrontendCompanyTestingWorkflowInfoHierarchyNodes>,
	): JSX.Element {
		// 1️⃣ Flatten hierarchy into all info fields (column definitions)
		function collectInfoFields(
			nodes: Array<FrontendCompanyTestingWorkflowInfoHierarchyNodes>,
		): Array<{
			type: "field" | "instanceId" | "result";
			field?: FrontendCompanyTestingWorkflowInfoFields;
			nodeId: string;
			nodeName: string;
			displayOrder: number;
		}> {
			const result: Array<{
				type: "field" | "instanceId" | "result";
				field?: FrontendCompanyTestingWorkflowInfoFields;
				nodeId: string;
				nodeName: string;
				displayOrder: number;
			}> = [];

			for (const node of nodes) {
				// Add fixed-value placements
				if (
					node.nodeType ===
					TestingWorkflowInfoHierarchyNodeType.INSTANCE_ID_PLACEMENT
				) {
					result.push({
						type: "instanceId",
						nodeId: node.nodeId,
						nodeName: node.nodeName,
						displayOrder: node.nodeDisplayOrder,
					});
				} else if (
					node.nodeType ===
					TestingWorkflowInfoHierarchyNodeType.RESULT_PLACEMENT
				) {
					result.push({
						type: "result",
						nodeId: node.nodeId,
						nodeName: node.nodeName,
						displayOrder: node.nodeDisplayOrder,
					});
				}

				// Regular info fields
				if (node.infoFields && node.infoFields.length > 0) {
					for (const field of node.infoFields) {
						result.push({
							type: "field",
							field,
							nodeId: node.nodeId,
							nodeName: node.nodeName,
							displayOrder: node.nodeDisplayOrder,
						});
					}
				}

				// Recurse into children
				if (node.children && node.children.length > 0) {
					result.push(...collectInfoFields(node.children));
				}
			}

			return result;
		}

		const allInfoFields = collectInfoFields(rootNodes).sort((a, b) => {
			return a.displayOrder - b.displayOrder;
		});
		// 2️⃣ Build rows for each instance
		const rows = batchDetails.instances.map((instance, rowIndex) => {
			// const instanceWorkflowInfo: Array<FrontendBatchInstanceLabWorkflowInfo> =
			// 	batchDetails.batchWorkflowInformation.batchWorkflowInfos.filter(
			// 		(info) => {
			// 			return info.instanceId == instance.sampleInstanceId;
			// 		}
			// 	);

			const cells = allInfoFields.map((item, colIndex) => {
				let inputField: JSX.Element | null = null;

				switch (item.type) {
					case "instanceId":
						const value: string = instance.sample.fullSampleId || "";
						inputField = <div style={{ textAlign: "center" }}>{value}</div>;
						break;

					case "result":
						const matchingResultField: FrontendCompanyWorkflowResultFields = (
							batchDetails.workflowStructure
								?.resultFields as Array<FrontendCompanyWorkflowResultFields>
						).find((field) => {
							return field.hierarchyNodeId === item.nodeId;
						}) as FrontendCompanyWorkflowResultFields;

						const recordedResult: FrontendTestResult | undefined =
							resultValues[instance.sampleInstanceId]?.[
								matchingResultField.resultFieldId
							];

						const liveValues: Record<string, string> =
							liveFormulaValuesByInstance[instance.sampleInstanceId][
								matchingResultField.resultFieldId
							];
						const setResultDeviationModalOpen = (open: boolean) => {
							if (open) {
								setDeviateInstanceId(instance.sampleInstanceId);
								setResultToDeviate(recordedResult);
							} else {
								setDeviateInstanceId(undefined);
								setResultToDeviate(undefined);
							}
							setDeviationModalOpen(open);
						};
						inputField = (
							<WorkflowResultField
								company={company}
								resultField={matchingResultField}
								relatedValues={liveValues}
								instanceId={instance.sampleInstanceId}
								currentIteration={batchDetails.currentIteration}
								recordedResult={recordedResult}
								hasDeviation={recordedResult?.hasDeviation || false}
								setRecordedResult={(updatedResult: FrontendTestResult) => {
									updateResultValue(
										instance.sampleInstanceId,
										matchingResultField.resultFieldId,
										updatedResult,
									);
								}}
								setResultDeviationModalOpen={setResultDeviationModalOpen}
							></WorkflowResultField>
						);

						break;

					case "field":
						// const matchingValue: FrontendBatchInstanceLabWorkflowInfo |undefined = instanceWorkflowInfo.find((info) => {
						// 	return item.field?.infoId === info.infoId;
						// });
						if (item.field) {
							inputField = renderWorkflowInputField(
								item.field,
								instance.sampleInstanceId,
							);
						}
						break;
				}

				return <td key={`${rowIndex}-${colIndex}`}>{inputField}</td>;
			});

			return <tr key={rowIndex}>{cells}</tr>;
		});

		// 3️⃣ Wrap in tbody
		return <tbody>{rows}</tbody>;
	}

	return (
		<>
			{renderingStepSignatures.length > 0 && (
				<div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
					<div className="typography-heading-03">
						{`${translation("SignedBy")}: `}
					</div>
					{renderingStepSignatures.map((signature) => {
						return (
							<div>
								<div>{`${signature.signedUser.firstName} ${signature.signedUser.lastName}`}</div>
								<div>{formatCustomDate(signature.inserted as Date)}</div>
							</div>
						);
					})}
				</div>
			)}
			{rootNode && renderWorkflowHierarchyNode(rootNode, 0)}
			{resultToDeviate && (
				<WorkflowResultDeviationModal
					open={deviationModalOpen}
					setOpen={setDeviationModalOpen}
					recordedResult={resultToDeviate as FrontendTestResult}
					proceedAction={(updatedResult: FrontendTestResult) => {
						updateResultValue(
							deviateInstanceId as string,
							resultToDeviate.resultFieldId,
							updatedResult,
						);
					}}
				/>
			)}
			<WorkflowSignatureModal
				user={user}
				open={signModalOpen}
				disableBatchEdit={signModalDisableEdit}
				setOpen={setSignModalOpen}
				proceedAction={signModalProceedFunction}
			></WorkflowSignatureModal>
		</>
	);
}
